
import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getWalletBalance } from '@/services/walletService';

interface AnalysisConfig {
  cost: number;
  functionName: string;
  minimumBalance?: number;
}

export const useAdvancedAnalysis = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [userBalance, setUserBalance] = useState<number | null>(null);
  const { toast } = useToast();

  const loadBalance = useCallback(async () => {
    try {
      const balance = await getWalletBalance();
      setUserBalance(balance);
      return balance;
    } catch (error) {
      console.error('Erro ao carregar saldo:', error);
      return null;
    }
  }, []);

  const generateAnalysis = useCallback(async (
    data: any,
    config: AnalysisConfig
  ) => {
    try {
      setIsGenerating(true);
      
      const currentBalance = userBalance ?? await loadBalance();
      const minimumRequired = config.minimumBalance ?? config.cost;
      
      if (currentBalance === null || currentBalance < minimumRequired) {
        toast({
          title: "Saldo Insuficiente",
          description: `Você precisa de pelo menos ${minimumRequired} Seshs para gerar esta análise.`,
          variant: "destructive",
        });
        return null;
      }

      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      const { data: response, error } = await supabase.functions.invoke(
        config.functionName,
        {
          body: data,
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          }
        }
      );

      if (error) {
        throw new Error(error.message || 'Erro na função de análise');
      }

      if (response?.error) {
        if (response.requiresPayment) {
          toast({
            title: "Saldo Insuficiente",
            description: response.error,
            variant: "destructive",
          });
          return null;
        }
        throw new Error(response.error);
      }

      // Atualizar saldo local
      await loadBalance();

      toast({
        title: "Análise Gerada com Sucesso!",
        description: `Sua análise está pronta. Foram debitados ${response.cost || config.cost} Seshs.`,
      });

      return response;

    } catch (error) {
      console.error('Erro na análise:', error);
      
      let errorMessage = "Ocorreu um erro inesperado. Tente novamente.";
      
      if (error instanceof Error) {
        if (error.message.includes('insufficient') || error.message.includes('Saldo insuficiente')) {
          errorMessage = "Saldo insuficiente para gerar a análise.";
        } else if (error.message.includes('unauthorized') || error.message.includes('Unauthorized')) {
          errorMessage = "Erro de autenticação. Faça login novamente.";
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = "Erro de conexão. Verifique sua internet e tente novamente.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Erro ao Gerar Análise",
        description: errorMessage,
        variant: "destructive",
      });
      
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [userBalance, loadBalance, toast]);

  return {
    isGenerating,
    userBalance,
    loadBalance,
    generateAnalysis
  };
};
